import { getFirebaseAuth } from '@/lib/firebase';
import { mapApiErrorMessage } from '@/lib/apiErrors';
import { getApiBaseUrl } from '@/lib/workerApi';

const base = getApiBaseUrl();

async function authHeaders(): Promise<HeadersInit> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
}

export async function apiGet<T>(path: string): Promise<T> {
  if (!base) throw new Error('VITE_API_URL is not configured');
  const res = await fetch(`${base}${path}`, {
    method: 'GET',
    headers: await authHeaders()
  });
  const data = await parseJsonBody<T>(res);
  if (!res.ok) {
    throw new ApiHttpError(apiErrorMessage(data, res), res.status);
  }
  return data;
}

function apiErrorMessage(data: { error?: unknown }, res: Response): string {
  if (typeof data.error === 'string' && data.error.trim()) {
    return mapApiErrorMessage(data.error);
  }
  if (res.status === 404) {
    return mapApiErrorMessage('Not found');
  }
  return mapApiErrorMessage(res.statusText || 'Request failed');
}

export class ApiHttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
  }
}

async function parseJsonBody<T>(res: Response): Promise<T & { error?: string }> {
  const text = await res.text();
  if (!text) return {} as T & { error?: string };
  try {
    return JSON.parse(text) as T & { error?: string };
  } catch {
    throw new Error(res.ok ? 'Invalid API response' : res.statusText || 'Request failed');
  }
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  if (!base) throw new Error('VITE_API_URL is not configured');
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseJsonBody<T>(res);
  if (!res.ok) {
    throw new ApiHttpError(apiErrorMessage(data, res), res.status);
  }
  return data;
}
